'use client';

import React, { useEffect, useState } from 'react';
import AppLogo from '@/components/ui/AppLogo';

interface SplashScreenProps {
  onComplete?: () => void;
  duration?: number;
}

export default function SplashScreen({ onComplete, duration = 1600 }: SplashScreenProps) {
  const [phase, setPhase] = useState<'enter' | 'hold' | 'exit'>('enter');

  useEffect(() => {
    const enterTimer = setTimeout(() => setPhase('hold'), 400);
    const holdTimer = setTimeout(() => setPhase('exit'), duration - 300);
    const exitTimer = setTimeout(() => onComplete?.(), duration);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
    };
  }, [duration, onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background transition-opacity duration-300 ${
        phase === 'exit' ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      aria-hidden="true"
    >
      {/* Subtle background texture */}
      <div className="absolute inset-0 pattern-adire opacity-30 pointer-events-none" />

      {/* Centered content */}
      <div
        className={`flex flex-col items-center gap-5 transition-all duration-500 ${
          phase === 'enter' ?'opacity-0 scale-95 translate-y-2'
            : phase === 'hold' ?'opacity-100 scale-100 translate-y-0' :'opacity-0 scale-105 -translate-y-1'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        {/* Logo mark */}
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
            <AppLogo size={52} />
          </div>
          {/* Subtle ring */}
          <div
            className="absolute -inset-2 rounded-3xl border border-primary/15"
            style={{
              animation: phase === 'hold' ? 'pulseGlow 2s ease-in-out infinite' : 'none',
            }}
          />
        </div>

        {/* Brand name */}
        <div className="text-center">
          <h1 className="text-xl font-bold text-foreground tracking-tight">Naija Explorer</h1>
          <p className="text-xs text-muted-foreground mt-1 font-medium">Discover Nigeria, Learn for Life</p>
        </div>

        {/* Loading dots */}
        <div className="flex gap-1.5 mt-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-primary/50"
              style={{
                animation: phase === 'hold' ? `bounce 0.9s ease-in-out ${i * 0.15}s infinite` : 'none',
              }}
            />
          ))}
        </div>
      </div>

      {/* Nigerian flag accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 flex">
        <div className="flex-1 bg-primary" />
        <div className="flex-1 bg-background" />
        <div className="flex-1 bg-primary" />
      </div>
    </div>
  );
}
